import { useState, useCallback, useMemo } from "react";
import { Text, TextField, Flex } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { CopyRelease } from "@/v2/features/backoffice/CopyRelease";
import { ReclassifyTopics } from "@/v2/features/backoffice/ReclassifyTopics";
import { useNavigate } from "react-router-dom";

interface Action {
  name: string;
  title: string;
  description: string;
}

const backofficeActions: Action[] = [
  {
    name: "copy-release",
    title: "Copy Release",
    description: "Copy a release to another agent",
  },
  {
    name: "reclassify-topics",
    title: "Reclassify Topics",
    description: "Reclassify topics' tickets",
  },
];

interface ActionCardProps {
  action: Action;
  isSelected: boolean;
  onClick: () => void;
}

function ActionCard({ action, isSelected, onClick }: ActionCardProps) {
  return (
    <Flex
      key={action.name}
      direction="column"
      gap="2"
      p="4"
      style={{
        borderRadius: 8,
        border: "1px solid var(--gray-6)",
        minWidth: 220,
        background: "var(--color-panel-solid)",
        cursor: "pointer",
        outline: isSelected ? "2px solid var(--accent-9)" : undefined,
      }}
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
    >
      <Text size="4" weight="bold">
        {action.title}
      </Text>
      <Text size="2" color="gray">
        {action.description}
      </Text>
    </Flex>
  );
}

function AdminDashboard() {
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const handleCardClick = useCallback((action: Action) => {
    setSelectedAction(action);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedAction(null);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const filteredActions = useMemo(() => {
    if (!searchQuery.trim()) {
      return backofficeActions;
    }

    const query = searchQuery.toLowerCase();
    return backofficeActions.filter(
      (action) =>
        action.title.toLowerCase().includes(query) ||
        action.description.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <>
      <Flex direction="column" gap="4" p="4">
        <Text size="5" weight="bold">
          Admin Dashboard
        </Text>
        <TextField.Root
          placeholder="Search actions..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <Flex direction="row" gap="4" wrap="wrap">
          {filteredActions.map((action) => (
            <ActionCard
              key={action.name}
              action={action}
              isSelected={selectedAction?.name === action.name}
              onClick={() => handleCardClick(action)}
            />
          ))}
          <ActionCard
            key={"tenant-statistics"}
            action={{
              name: "tenant-statistics",
              title: "Tenant Statistics",
              description: "View statistics across all tenants",
            }}
            isSelected={false}
            onClick={() => {
              navigate("/admin-dashboard/tenants/statistics");
            }}
          />
          <ActionCard
            key={"billing-information"}
            action={{
              name: "billing-information",
              title: "Billing Information",
              description: "View and manage billing information",
            }}
            isSelected={false}
            onClick={() => {
              navigate("/admin-dashboard/billing");
            }}
          />
        </Flex>
      </Flex>

      {selectedAction != null && (
        <Dialog open>
          <Dialog.Content>
            <Dialog.Title>{selectedAction.title}</Dialog.Title>

            <Dialog.Close
              className=" absolute top-4 right-4"
              onClick={handleCloseDialog}
            />

            {selectedAction.name === "copy-release" && (
              <CopyRelease onClose={handleCloseDialog} />
            )}

            {selectedAction.name === "reclassify-topics" && (
              <ReclassifyTopics onClose={handleCloseDialog} />
            )}
          </Dialog.Content>
        </Dialog>
      )}
    </>
  );
}

export default AdminDashboard;
